/**
 * PDF availability helper.
 *
 * Centralises the logic for determining whether a document entry's PDF is
 * ready to preview or download. Every component that previously checked
 * `docEntry.pdfPath` truthiness should use `getPdfAvailability` instead,
 * so the new `pdfStatus` field (added by the PDF rendering resilience work)
 * is respected consistently across the platform.
 *
 * The function handles four states:
 *
 *   - "ready"   -- PDF exists and is available for preview/download.
 *   - "pending" -- PDF is being prepared in the background (PR 2 retry).
 *   - "error"   -- PDF generation failed; the clinician can try again.
 *   - "missing" -- No document entry exists, or no render has happened yet.
 *
 * Legacy data model compatibility: entries written before the resilience
 * work have no `pdfStatus` field. Those are treated as "ready" when
 * `pdfPath` is truthy (there is a valid blob behind the path), and
 * "missing" otherwise. This means no data migration is required.
 */

/**
 * Determine the availability state of a document entry's PDF.
 *
 * @param {Object|null|undefined} docEntry - The `documents.<key>` map from
 *   the patient's Firestore document, e.g. `patientData.documents.patientReport`.
 *   May be null/undefined when the entry does not exist.
 *
 * @returns {"ready"|"pending"|"error"|"missing"} One of four states:
 *   - "ready":   The PDF blob exists and can be previewed/downloaded.
 *   - "pending": A background retry is in progress (pdfStatus === "pending").
 *   - "error":   Generation failed (pdfStatus === "error" or pdfPath is null
 *                with no pending status).
 *   - "missing": The document entry does not exist at all.
 */
export function getPdfAvailability(docEntry) {
  // No entry at all -- the report has never been rendered.
  if (!docEntry) return 'missing';

  const { pdfPath, pdfStatus } = docEntry;

  // Explicit pdfStatus takes precedence when present (new data model).
  if (pdfStatus === 'pending') return 'pending';
  if (pdfStatus === 'error') return 'error';
  if (pdfStatus === 'ready') return 'ready';

  // Legacy fallback: no pdfStatus field. pdfPath truthy means the blob
  // exists from a pre-resilience render; falsy means it was never created.
  if (pdfPath) return 'ready';

  return 'missing';
}

/**
 * Convenience predicate: is the PDF available for preview/download?
 *
 * @param {Object|null|undefined} docEntry
 * @returns {boolean}
 */
export function isPdfReady(docEntry) {
  return getPdfAvailability(docEntry) === 'ready';
}

/**
 * Human-readable label for the current PDF state.
 * Used in dashboard rows to explain why Preview/Download is disabled.
 *
 * @param {Object|null|undefined} docEntry
 * @returns {string|null} A label string, or null when the PDF is ready
 *   (no label needed).
 */
export function getPdfStatusLabel(docEntry) {
  const state = getPdfAvailability(docEntry);
  switch (state) {
    case 'pending':
      return 'Preparing PDF';
    case 'error':
      return 'PDF could not be created';
    case 'missing':
      return null; // No label when the report was never rendered
    case 'ready':
    default:
      return null;
  }
}
